import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Lead, LeadUpdateRequest } from '../../../core/models/lead.model';
import { LeadOrigin, ReferralChannel } from '../../../core/models/reference-data.model';
import { MultiSelectDropdownComponent } from '../multi-select-dropdown/multi-select-dropdown.component';
import { CustomAttributesViewComponent } from '../custom-attributes-view/custom-attributes-view.component';
import { CustomAttributesFormComponent } from '../custom-attributes-form/custom-attributes-form.component';
import { GooglePlacesAutocompleteComponent } from '../google-places-autocomplete/google-places-autocomplete.component';
import { GoogleMapComponent } from '../google-map/google-map.component';
import { MultiSelectOption } from '../../../core/models/select.models';
import { CustomAttributeDefinition, CustomAttribute } from '../../../core/models/endeavor.models';
import { getStateFullName } from '../../constants/states.constants';
import { getCountryCode } from '../../constants/countries.constants';

@Component({
  selector: 'app-lead-details-card',
  standalone: true,
  imports: [CommonModule, FormsModule, MultiSelectDropdownComponent, CustomAttributesViewComponent, CustomAttributesFormComponent, GooglePlacesAutocompleteComponent, GoogleMapComponent],
  templateUrl: './lead-details-card.component.html',
  styleUrls: ['./lead-details-card.component.scss']
})
export class LeadDetailsCardComponent implements OnChanges {
  @Input() lead?: Lead;
  @Input() leadOrigins: LeadOrigin[] = [];
  @Input() referralChannels: ReferralChannel[] = [];
  @Input() labelTags: any[] = [];
  @Input() customAttributeDefinitions: CustomAttributeDefinition[] = [];
  @Output() editClick = new EventEmitter<void>();
  @Output() expandClick = new EventEmitter<void>();
  @Output() saveClick = new EventEmitter<LeadUpdateRequest>();
  @Output() cancelClick = new EventEmitter<void>();

  isExpanded = false; // Local expand state for the card
  isEditing = false; // Edit state for the card

  @ViewChild(CustomAttributesFormComponent) customAttributesFormComponent?: CustomAttributesFormComponent;

  // Edit form values
  editReferralChannelId: number | undefined;
  editLeadOriginId: number | undefined;
  editLabelIds: string[] = [];
  editLocation = ''; // Location/Address field

  onEditClick(): void {
    if (!this.isEditing) {
      // Auto-expand the card when editing starts
      if (!this.isExpanded) {
        this.isExpanded = true;
      }
      this.startEditing();
    }
  }

  onExpandClick(): void {
    this.isExpanded = !this.isExpanded;
    this.expandClick.emit();
  }

  onSaveClick(): void {
    if (!this.lead) return;

    // Include all existing lead fields to prevent data loss
    // Note: Do not include projectValue - this card doesn't edit it, and including it
    // causes incorrect history log entries (e.g., "value changed from '0.00' to '0'")
    const projectTypeIds = this.getProjectTypeIds();
    const updateRequest: LeadUpdateRequest = {
      id: this.lead.id,
      title: this.lead.title,
      description: this.lead.description,
      email: this.lead.email,
      firstName: this.lead.firstName,
      middleName: this.lead.middleName,
      lastName: this.lead.lastName,
      projectTypeIds: projectTypeIds.filter(id => id != null && id !== 0),
      // projectValue is NOT included - this card doesn't edit it
      temperatureTypeId: this.lead.temperatureTypeId,
      projectOwnerId: this.lead.projectOwnerId ?? undefined,
      // Update referral channel and origin with edited values
      referralChannelId: this.editReferralChannelId ?? this.lead.referralChannelId ?? undefined,
      leadOriginId: this.editLeadOriginId ?? this.lead.leadOriginId ?? undefined,
      // Preserve all address fields
      street1: this.lead.street1,
      street2: this.lead.street2,
      city: this.lead.city,
      state: this.lead.state,
      zip: this.lead.zip,
      countryCode: this.lead.countryCode,
      country: this.lead.country,
      // Preserve other fields
      isFlagged: this.lead.isFlagged,
      isArchived: this.lead.isArchived,
      isDeleted: this.lead.isDeleted
    };
    
    // Store address data from Google Places if available
    if ((this as any).lastAddressData) {
      const addressData = (this as any).lastAddressData;
      updateRequest.street1 = addressData.street1 || updateRequest.street1;
      updateRequest.city = addressData.city || updateRequest.city;
      updateRequest.state = addressData.state ? getStateFullName(addressData.state) : updateRequest.state;
      updateRequest.zip = addressData.zip || updateRequest.zip;
      updateRequest.country = addressData.country || updateRequest.country;
      updateRequest.countryCode = addressData.country ? getCountryCode(addressData.country) : updateRequest.countryCode;
    }

    // Always include labels in the update request (even if empty array)
    // This allows the parent component to detect label changes
    if (this.editLabelIds && this.editLabelIds.length > 0) {
      // Convert selected label IDs to Label objects
      const selectedLabels = this.editLabelIds.map(id => {
        const labelTag = this.labelTags.find(tag => tag.labelTagId.toString() === id);
        if (labelTag) {
          return {
            labelTagId: labelTag.labelTagId,
            name: labelTag.name,
            labelType: labelTag.labelType || '',
            colorCode: labelTag.colorCode || '#808080'
          };
        }
        return null;
      }).filter(label => label !== null) as any[];
      
      updateRequest.labels = selectedLabels;
    } else {
      // Empty array means user removed all labels
      updateRequest.labels = [];
    }

    // Get custom attributes from the form component
    // Only include custom attributes if the form component returns them (meaning they were changed)
    // Don't preserve existing custom attributes if form returns empty - this prevents sending unchanged data
    if (this.customAttributesFormComponent) {
      const customAttributes = this.customAttributesFormComponent.getFormData();
      if (customAttributes && customAttributes.length > 0) {
        // Add entityId to each custom attribute
        const customAttributesWithEntityId = customAttributes.map(attr => ({
          ...attr,
          entityId: this.lead?.id || 0
        }));
        (updateRequest as any).customAttributes = customAttributesWithEntityId;
      }
      // Don't preserve existing custom attributes - only send if form component returns changed ones
    }

    this.saveClick.emit(updateRequest);
    // Collapse card and exit edit mode after save
    this.isEditing = false;
    this.isExpanded = false;
  }

  /**
   * Parse address string into components
   * Format: "street1, street2, city, state zip" or "street1, city, state zip"
   */
  private parseAddress(addressString: string): { street1?: string; street2?: string; city?: string; state?: string; zip?: string } {
    if (!addressString || !addressString.trim()) {
      return {};
    }

    // Remove extra whitespace and split by comma
    const parts = addressString.split(',').map(part => part.trim()).filter(part => part.length > 0);
    
    if (parts.length === 0) {
      return { street1: addressString };
    }

    // Last part typically contains state and zip (e.g., "CA 90210" or "California 90210")
    const lastPart = parts[parts.length - 1];
    const stateZipMatch = lastPart.match(/(.+?)\s+(\d{5}(?:-\d{4})?)$/);
    
    let state = '';
    let zip = '';
    let city = '';
    
    if (stateZipMatch) {
      state = stateZipMatch[1].trim();
      zip = stateZipMatch[2].trim();
      // City is the second-to-last part if we have state/zip
      if (parts.length >= 2) {
        city = parts[parts.length - 2];
      }
    } else {
      // No zip found, assume last part is state or city
      if (parts.length >= 2) {
        city = parts[parts.length - 2];
        state = parts[parts.length - 1];
      } else if (parts.length === 1) {
        city = parts[0];
      }
    }

    // Street address is the first part(s)
    let street1 = parts[0] || '';
    let street2: string | undefined = undefined;
    
    // If we have 4+ parts and found state/zip, second part might be street2
    if (parts.length >= 4 && stateZipMatch) {
      street2 = parts[1];
    } else if (parts.length >= 3 && !stateZipMatch) {
      // Without state/zip, if 3+ parts, second might be street2
      street2 = parts[1];
    }

    return {
      street1: street1 || undefined,
      street2: street2,
      city: city || undefined,
      state: state || undefined,
      zip: zip || undefined
    };
  }

  onCancelClick(): void {
    this.isEditing = false;
    this.isExpanded = false; // Collapse card when canceling
    this.cancelClick.emit();
  }

  private startEditing(): void {
    this.isEditing = true;
    // Initialize form values from lead
    this.editReferralChannelId = this.lead?.referralChannelId ?? undefined;
    this.editLeadOriginId = this.lead?.leadOriginId;
    
    // Initialize labels from lead
    this.editLabelIds = this.getLabelsForForm(this.lead?.labels || []);
    
    // Initialize location/address from lead
    this.editLocation = this.getAddress();
  }


  /**
   * Handle address selection from Google Places
   */
  onAddressSelected(addressData: {
    street1: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }): void {
    // Store address data for use in onSaveClick
    (this as any).lastAddressData = addressData;
    
    // Update the location string to show the full address
    const parts = [
      addressData.street1,
      addressData.city,
      addressData.state,
      addressData.zip
    ].filter(part => part && part.trim() !== '');
    this.editLocation = parts.join(', ');
  }

  /** Project type IDs from backend (array or single for backward compatibility). */
  getProjectTypeIds(): number[] {
    const l = this.lead;
    if (!l) return [];
    if (l.projectTypeIds?.length) {
      return l.projectTypeIds.filter(id => id != null && id !== 0);
    }
    if (l.projectTypeId != null && l.projectTypeId !== undefined && l.projectTypeId !== 0) {
      return [l.projectTypeId];
    }
    return [];
  }

  getLabelsForForm(labels: any[]): string[] {
    if (!labels || !Array.isArray(labels)) return [];
    return labels
      .filter(label => label && label.labelTagId !== undefined && label.labelTagId !== null)
      .map(label => label.labelTagId.toString());
  }

  get labelSelectOptions(): MultiSelectOption[] {
    return this.labelTags.map(labelTag => ({
      value: labelTag.labelTagId.toString(),
      label: labelTag.name,
      colorCode: labelTag.colorCode
    }));
  }

  getCustomAttributeValue(displayName: string): number | undefined {
    if (!this.lead?.customAttributes) return undefined;
    const attr = this.lead.customAttributes.find(a => a.displayName === displayName);
    return attr?.optionId || attr?.numberValue || undefined;
  }

  getCustomAttributeStringValue(displayName: string): string | null {
    if (!this.lead?.customAttributes) return null;
    const attr = this.lead.customAttributes.find(a => a.displayName === displayName);
    return attr?.stringValue || null;
  }

  getCustomAttributeDateValue(displayName: string): string | null {
    if (!this.lead?.customAttributes) return null;
    const attr = this.lead.customAttributes.find(a => a.displayName === displayName);
    return attr?.dateValue || null;
  }

  getCustomAttributeOptions(displayName: string): Array<{id: number, label: string}> {
    if (!this.customAttributeDefinitions || this.customAttributeDefinitions.length === 0) {
      return [];
    }
    
    // Find the custom attribute definition by display name
    const definition = this.customAttributeDefinitions.find(def => def.displayName === displayName);
    if (!definition || !definition.options) {
      return [];
    }
    
    // Return the options in the format expected by the template
    return definition.options.map(option => ({
      id: option.id,
      label: option.label
    }));
  }

  // Convert lead origins to dropdown options
  get leadOriginOptions(): any[] {
    const options = this.leadOrigins.map(origin => ({
      label: origin.name,
      value: origin.leadOriginId
    }));
    return [{ label: 'Select', value: null }, ...options];
  }

  // Convert referral channels to dropdown options
  get referralChannelOptions(): any[] {
    const options = this.referralChannels.map(channel => ({
      label: channel.name,
      value: channel.referralChannelId
    }));
    return [{ label: 'Select', value: null }, ...options];
  }

  // Convert custom attribute options to dropdown format
  getCustomAttributeDropdownOptions(displayName: string): any[] {
    const options = this.getCustomAttributeOptions(displayName);
    return [{ label: 'Select', value: null }, ...options.map(opt => ({ label: opt.label, value: opt.id }))];
  }

  getLabels() {
    return this.lead?.labels || [];
  }

  getReferralSource(): string {
    if (!this.lead?.referralChannelId) return '-';
    const channel = this.referralChannels.find(rc => rc.referralChannelId === this.lead?.referralChannelId);
    return channel?.name || '-';
  }

  getOrigin(): string {
    if (!this.lead?.leadOriginId) return '-';
    const origin = this.leadOrigins.find(lo => lo.leadOriginId === this.lead?.leadOriginId);
    return origin?.name || '-';
  }

  getAddress(): string {
    if (!this.lead) return '-';
    const parts = [
      this.lead.street1,
      this.lead.street2,
      this.lead.city,
      this.lead.state,
      this.lead.zip
    ].filter(part => part && part.trim() !== '');
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  // Get color code from label (label has colorCode property)
  getLabelName(label: any): string {
    return label.name || '-';
  }

  getGoogleMapsUrl(): string {
    if (!this.lead) return '#';
    const address = this.getAddress();
    if (address === '-') return '#';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If lead is updated externally (after save), reset editing state
    if (changes['lead'] && !changes['lead'].firstChange && this.isEditing) {
      // Lead was updated, assume save was successful, reset editing state
      this.isEditing = false;
      this.isExpanded = false; // Collapse card after save
    }
  }
}
